import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-ink border border-bone/15 px-4 py-3.5 text-bone placeholder:text-ash/60 focus:border-saffron focus:outline-none transition-colors";

function FieldInput({ field, value, onChange, onToggle }) {
  const id = `lead-field-${field.id}`;
  if (field.type === "long_text") {
    return <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={inputCls} data-testid={id} />;
  }
  if (field.type === "radio") {
    return (
      <div className="space-y-2" data-testid={id}>
        {field.options.map((opt) => (
          <label key={opt} className="flex items-center gap-3 text-sm text-bone/85 cursor-pointer group">
            <input type="radio" name={field.id} checked={value === opt} onChange={() => onChange(opt)}
              className="accent-[#D97736] w-4 h-4" data-testid={`${id}-${opt.replace(/\W+/g, "-").toLowerCase()}`} />
            <span className="group-hover:text-bone transition-colors">{opt}</span>
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "checkboxes") {
    const cur = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2" data-testid={id}>
        {field.options.map((opt) => (
          <label key={opt} className="flex items-center gap-3 text-sm text-bone/85 cursor-pointer group">
            <input type="checkbox" checked={cur.includes(opt)} onChange={() => onToggle(opt)}
              className="accent-[#D97736] w-4 h-4" data-testid={`${id}-${opt.replace(/\W+/g, "-").toLowerCase()}`} />
            <span className="group-hover:text-bone transition-colors">{opt}</span>
          </label>
        ))}
      </div>
    );
  }
  if (field.type === "dropdown") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid={id}>
        <option value="" className="bg-ink">{field.placeholder || "Select…"}</option>
        {field.options.map((opt) => <option key={opt} value={opt} className="bg-ink">{opt}</option>)}
      </select>
    );
  }
  if (field.type === "date") {
    return <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid={id} />;
  }
  if (field.type === "phone") {
    return <input type="tel" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || "10-digit mobile number"} className={inputCls} data-testid={id} />;
  }
  if (field.type === "email") {
    return <input type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || "you@example.com"} className={inputCls} data-testid={id} />;
  }
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={inputCls} data-testid={id} />;
}

export default function LeadFormPage() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | unavailable | submitted
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/lead-forms/${slug}`)
      .then(({ data }) => {
        setForm(data);
        setState("ready");
        document.title = `${data.name} — The Band Sumiran`;
      })
      .catch(() => setState("unavailable"));
  }, [slug]);

  const setVal = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));
  const toggleCheck = (id, opt) => {
    setValues((prev) => {
      const cur = Array.isArray(prev[id]) ? prev[id] : [];
      return { ...prev, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const f of form.fields) {
      const v = values[f.id];
      const empty = f.type === "checkboxes" ? !(Array.isArray(v) && v.length) : !String(v || "").trim();
      if (f.required && empty) {
        toast.error(`'${f.label}' is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post(`/lead-forms/${slug}/submit`, { values });
      setState("submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err, "Could not submit — please try again"));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="lead-form-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10">
        <Link to="/" className="flex items-baseline gap-2" data-testid="lead-form-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
      </header>

      <main className="px-6 md:px-12 py-14 md:py-20 max-w-xl mx-auto w-full">
        {state === "loading" && (
          <p className="text-ash flex items-center gap-2" data-testid="lead-form-loading"><Loader2 size={16} className="animate-spin" /> Loading…</p>
        )}

        {state === "unavailable" && (
          <div className="text-center py-16" data-testid="lead-form-unavailable">
            <p className="font-hindi text-3xl text-saffron/60">सुमिरन</p>
            <h1 className="mt-4 font-serif text-3xl text-bone">This form is unavailable</h1>
            <p className="mt-3 text-sm text-ash">The link may be incorrect or the form is no longer accepting responses.</p>
          </div>
        )}

        {state === "submitted" && (
          <div className="text-center py-16" data-testid="lead-form-success">
            <CheckCircle2 size={44} className="mx-auto text-saffron" />
            <h1 className="mt-5 font-serif text-3xl text-bone">Thank you!</h1>
            <p className="mt-3 text-sm text-ash leading-relaxed">
              We've received your details{form?.fields?.some((f) => f.type === "phone") ? " — a confirmation is on its way to your WhatsApp" : ""}.
              The band will be in touch soon.
            </p>
          </div>
        )}

        {state === "ready" && form && (
          <>
            <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">The Band Sumiran</p>
            <h1 className="mt-3 font-serif text-4xl text-bone leading-tight" data-testid="lead-form-title">{form.name}</h1>
            <form onSubmit={submit} className="mt-10 space-y-7" data-testid="lead-form">
              {form.fields.map((f) => (
                <div key={f.id} data-testid={`lead-field-wrap-${f.id}`}>
                  <label className="block text-xs font-display tracking-[0.2em] uppercase text-ash mb-2">
                    {f.label} {f.required && <span className="text-saffron">*</span>}
                  </label>
                  <FieldInput field={f} value={values[f.id]}
                    onChange={(v) => setVal(f.id, v)} onToggle={(opt) => toggleCheck(f.id, opt)} />
                </div>
              ))}
              <button type="submit" disabled={submitting}
                className="w-full py-4 bg-saffron text-ink font-medium tracking-wide hover:bg-gold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="lead-form-submit-btn">
                {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={15} />}
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
